import React, { useState } from 'react';
import { Plus, Gift, Edit3, Trash2, RotateCcw, Star, TrendingUp, Sparkles, Clock, Check, X, Info, ToggleLeft, ToggleRight } from 'lucide-react';
import { creditsAPI } from '../../services/api';

// ── bonus type meta ───────────────────────────────────────────────────────────
const BONUS_META = {
  WELCOME:   { label: 'Welcome',   icon: Star,       color: 'text-[var(--accent-primary)]',  bg: 'bg-[var(--accent-primary)]/10' },
  DEPOSIT:   { label: 'Deposit',   icon: TrendingUp, color: 'text-[var(--accent-success)]',  bg: 'bg-[var(--accent-success)]/10' },
  GOODWILL:  { label: 'Goodwill',  icon: Gift,       color: 'text-[var(--accent-warning)]',  bg: 'bg-[var(--accent-warning)]/10' },
  PROMOTION: { label: 'Promotion', icon: Sparkles,   color: 'text-[var(--accent-info)]',     bg: 'bg-[var(--accent-info)]/10' },
};

function BonusPill({ type }) {
  const m = BONUS_META[type] || BONUS_META.GOODWILL;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${m.color} ${m.bg}`}>
      <Icon className="w-2.5 h-2.5" />{m.label}
    </span>
  );
}

function StatusPill({ status }) {
  if (status === 'active')  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--accent-success)] bg-[var(--accent-success)]/10"><Check className="w-2.5 h-2.5" />Active</span>;
  if (status === 'revoked') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--accent-danger)] bg-[var(--accent-danger)]/10"><X className="w-2.5 h-2.5" />Revoked</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)] bg-[var(--text-tertiary)]/10"><Clock className="w-2.5 h-2.5" />Expired</span>;
}

function fmt(n) {
  return parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fromNow(iso) {
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const EMPTY_RULE  = { name: '', bonus_type: 'WELCOME', percentage: '', fixed_amount: '', max_credit: '', min_deposit: '', expires_days: '', is_active: true };
const EMPTY_AWARD = { accountId: '', userId: '', bonusType: 'GOODWILL', amount: '', notes: '' };

const fieldCls = "px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)] w-full";

// ── Rule form ─────────────────────────────────────────────────────────────────
function RuleForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial ? {
    name:         initial.name         || '',
    bonus_type:   initial.bonus_type   || 'WELCOME',
    percentage:   initial.percentage   || '',
    fixed_amount: initial.fixed_amount || '',
    max_credit:   initial.max_credit   || '',
    min_deposit:  initial.min_deposit  || '',
    expires_days: initial.expires_days || '',
    is_active:    initial.is_active ?? true,
  } : EMPTY_RULE);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isAuto = form.bonus_type === 'WELCOME' || form.bonus_type === 'DEPOSIT';

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave({
      name:        form.name.trim(),
      bonusType:   form.bonus_type,
      percentage:  form.percentage   || null,
      fixedAmount: form.fixed_amount || null,
      maxCredit:   form.max_credit   || null,
      minDeposit:  form.min_deposit  || null,
      expiresDays: form.expires_days || null,
      isActive:    form.is_active ? 1 : 0,
    });
  };

  return (
    <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
      <p className="text-xs font-semibold text-[var(--text-primary)]">
        {initial ? 'Edit' : 'New'} Bonus Rule
      </p>

      <input className={fieldCls} placeholder="Rule name (e.g. Welcome Bonus)"
        value={form.name} onChange={e => set('name', e.target.value)} />

      <select className={fieldCls + ' cursor-pointer'} value={form.bonus_type} onChange={e => set('bonus_type', e.target.value)}>
        <option value="WELCOME">WELCOME — First deposit only</option>
        <option value="DEPOSIT">DEPOSIT — Every deposit</option>
        <option value="GOODWILL">GOODWILL — Manual award</option>
        <option value="PROMOTION">PROMOTION — Campaign / fixed</option>
      </select>

      <div className="grid grid-cols-2 gap-2">
        {isAuto ? (
          <input className={fieldCls} type="number" min="0.01" max="100" step="0.01"
            placeholder="% of deposit (e.g. 20)"
            value={form.percentage} onChange={e => set('percentage', e.target.value)} />
        ) : (
          <input className={fieldCls} type="number" min="0.01" step="0.01"
            placeholder="Fixed amount (USD)"
            value={form.fixed_amount} onChange={e => set('fixed_amount', e.target.value)} />
        )}

        <input className={fieldCls} type="number" min="0.01" step="0.01"
          placeholder="Max credit cap (optional)"
          value={form.max_credit} onChange={e => set('max_credit', e.target.value)} />

        {isAuto && (
          <input className={fieldCls} type="number" min="0" step="0.01"
            placeholder="Min deposit to qualify"
            value={form.min_deposit} onChange={e => set('min_deposit', e.target.value)} />
        )}

        <input className={fieldCls} type="number" min="1" step="1"
          placeholder="Expires after N days (optional)"
          value={form.expires_days} onChange={e => set('expires_days', e.target.value)} />
      </div>

      <button type="button" onClick={() => set('is_active', !form.is_active)}
        className="flex items-center gap-2 text-xs transition-smooth">
        {form.is_active
          ? <ToggleRight className="w-5 h-5 text-[var(--accent-success)]" />
          : <ToggleLeft  className="w-5 h-5 text-[var(--text-tertiary)]" />}
        <span className={form.is_active ? 'text-[var(--accent-success)]' : 'text-[var(--text-tertiary)]'}>
          {form.is_active ? 'Active — auto-awards on qualifying deposits' : 'Disabled'}
        </span>
      </button>

      <div className="flex gap-2 pt-1">
        <button onClick={handleSave} disabled={saving || !form.name.trim()}
          className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg text-xs disabled:opacity-40 transition-smooth">
          {saving ? 'Saving…' : initial ? 'Save' : 'Add'}
        </button>
        <button onClick={onCancel} disabled={saving}
          className="px-4 py-2 border border-[var(--border-color)] rounded-lg text-xs text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-smooth disabled:opacity-40">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Manual award form ─────────────────────────────────────────────────────────
function AwardForm({ onAward, onCancel, saving }) {
  const [form, setForm] = useState(EMPTY_AWARD);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAward = () => {
    if (!form.accountId || !form.userId || !form.amount) return;
    onAward({
      accountId: parseInt(form.accountId),
      userId:    parseInt(form.userId),
      bonusType: form.bonusType,
      amount:    parseFloat(form.amount),
      notes:     form.notes || undefined,
    });
  };

  return (
    <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
      <p className="text-xs font-semibold text-[var(--text-primary)]">Manual Credit Award</p>

      <div className="grid grid-cols-2 gap-2">
        <input className={fieldCls} type="number" placeholder="Account ID"
          value={form.accountId} onChange={e => set('accountId', e.target.value)} />
        <input className={fieldCls} type="number" placeholder="User ID"
          value={form.userId} onChange={e => set('userId', e.target.value)} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select className={fieldCls + ' cursor-pointer'} value={form.bonusType} onChange={e => set('bonusType', e.target.value)}>
          <option value="GOODWILL">GOODWILL</option>
          <option value="PROMOTION">PROMOTION</option>
        </select>
        <input className={fieldCls} type="number" min="0.01" step="0.01" placeholder="Amount (USD)"
          value={form.amount} onChange={e => set('amount', e.target.value)} />
      </div>

      <input className={fieldCls} placeholder="Notes (optional)"
        value={form.notes} onChange={e => set('notes', e.target.value)} />

      <div className="flex gap-2 pt-1">
        <button onClick={handleAward} disabled={saving || !form.accountId || !form.userId || !form.amount}
          className="px-4 py-2 bg-[var(--accent-warning)] text-black rounded-lg text-xs font-semibold disabled:opacity-40 transition-smooth">
          {saving ? 'Awarding…' : 'Award Credit'}
        </button>
        <button onClick={onCancel} disabled={saving}
          className="px-4 py-2 border border-[var(--border-color)] rounded-lg text-xs text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-smooth disabled:opacity-40">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main panel — receives data as props from BrokerPluginsPage
// ═══════════════════════════════════════════════════════════════════════════════
export default function CreditSystemPanel({ credits, onRefresh }) {
  const [tab, setTab] = useState('rules');
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAward, setShowAward] = useState(false);
  const [saving, setSaving] = useState(false);

  // Use props data
  const rules = credits?.rules || [];
  const history = credits?.history || [];

  const handleOperation = async (operation) => {
    setSaving(true);
    setError(null);
    try {
      await operation();
      if (onRefresh) await onRefresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const createRule = async (data) => {
    await handleOperation(async () => {
      await creditsAPI.createRule(data);
      setShowForm(false);
    });
  };

  const updateRule = async (data) => {
    await handleOperation(async () => {
      await creditsAPI.updateRule(editing.id, data);
      setEditing(null);
    });
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Deactivate this rule?')) return;
    await handleOperation(() => creditsAPI.deleteRule(id));
  };

  const awardManual = async (data) => {
    await handleOperation(async () => {
      await creditsAPI.awardManual(data);
      setShowAward(false);
    });
  };

  const revokeCredit = async (id) => {
    const reason = window.prompt('Revoke reason (optional):');
    if (reason === null) return;
    await handleOperation(() => creditsAPI.revokeCredit(id, reason));
  };

  const totalAwarded = history.filter(h => h.status === 'active').reduce((s, h) => s + parseFloat(h.amount), 0);

  return (
    <div className="space-y-3">
      {/* Error display */}
      {error && (
        <div className="flex items-center justify-between gap-2 text-xs text-[var(--accent-danger)] bg-[var(--accent-danger)]/10 px-3 py-2 rounded-lg border border-[var(--accent-danger)]/20">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="hover:text-[var(--accent-danger-dark)]">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Active Rules',  val: rules.filter(r => r.is_active).length, color: 'text-[var(--accent-primary)]' },
          { label: 'Total Awarded', val: `$${fmt(totalAwarded)}`,               color: 'text-[var(--accent-success)]' },
          { label: 'Award Events',  val: history.length,                        color: 'text-[var(--text-primary)]' },
        ].map(({ label, val, color }) => (
          <div key={label} className="px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
            <p className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider mb-0.5">{label}</p>
            <p className={`text-base font-bold ${color}`}>{val}</p>
          </div>
        ))}
      </div>

      {/* Tab bar + action */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
          {[['rules', 'Bonus Rules'], ['history', 'Award History']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-smooth ${
                tab === id ? 'bg-[var(--accent-primary)] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'rules' && !showForm && !editing && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth">
            <Plus className="w-3.5 h-3.5" />New rule
          </button>
        )}
        {tab === 'history' && !showAward && (
          <button onClick={() => setShowAward(true)}
            className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth">
            <Gift className="w-3.5 h-3.5" />Manual award
          </button>
        )}
      </div>

      {/* ── RULES TAB ── */}
      {tab === 'rules' && (
        <div className="space-y-2">
          {showForm && !editing && (
            <RuleForm onSave={createRule} onCancel={() => setShowForm(false)} saving={saving} />
          )}

          {rules.length === 0 && !showForm && (
            <p className="text-xs text-[var(--text-tertiary)] py-2">
              No bonus rules yet. Add one to start auto-awarding credit on deposits.
            </p>
          )}

          {rules.map(r => (
            <div key={r.id}>
              {editing?.id === r.id ? (
                <RuleForm initial={r} onSave={updateRule} onCancel={() => setEditing(null)} saving={saving} />
              ) : (
                <div className={`flex items-center gap-3 px-3 py-2.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg ${!r.is_active ? 'opacity-60' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-medium text-[var(--text-primary)]">{r.name}</p>
                      <BonusPill type={r.bonus_type} />
                      {!r.is_active && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Disabled</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-[var(--text-secondary)]">
                      {r.percentage   && <span>🎯 {r.percentage}% of deposit</span>}
                      {r.fixed_amount && <span>💰 ${fmt(r.fixed_amount)} fixed</span>}
                      {r.max_credit   && <span>📈 Cap ${fmt(r.max_credit)}</span>}
                      {r.min_deposit  && <span>🔽 Min ${fmt(r.min_deposit)}</span>}
                      {r.expires_days && <span>⏳ {r.expires_days}d expiry</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setEditing(r); setShowForm(false); }} disabled={saving}
                      className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-smooth disabled:opacity-40">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteRule(r.id)} disabled={saving}
                      className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-danger)] hover:bg-[var(--accent-danger)]/10 transition-smooth disabled:opacity-40">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="flex gap-2 items-start p-2.5 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/15 mt-1">
            <Info className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              <strong className="text-[var(--accent-primary)]">WELCOME</strong> and{' '}
              <strong className="text-[var(--accent-primary)]">DEPOSIT</strong> rules fire automatically on deposit approval.{' '}
              <strong className="text-[var(--accent-primary)]">GOODWILL</strong> and{' '}
              <strong className="text-[var(--accent-primary)]">PROMOTION</strong> are awarded manually. Credit is wiped on liquidation.
            </p>
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <div className="space-y-2">
          {showAward && (
            <AwardForm onAward={awardManual} onCancel={() => setShowAward(false)} saving={saving} />
          )}

          {history.length === 0 && !showAward && (
            <p className="text-xs text-[var(--text-tertiary)] py-2">No credit awards yet.</p>
          )}

          {history.map(h => (
            <div key={h.id}
              className={`flex items-center gap-3 px-3 py-2.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg ${h.status === 'revoked' ? 'opacity-60' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-sm font-medium text-[var(--text-primary)]">${fmt(h.amount)}</span>
                  <span className="text-xs text-[var(--text-secondary)]">· Account #{h.account_id}</span>
                  <BonusPill type={h.bonus_type} />
                  <StatusPill status={h.status} />
                </div>
                <p className="text-xs text-[var(--text-tertiary)]">
                  {h.rule_name && `${h.rule_name} · `}{fromNow(h.created_at)}
                </p>
              </div>
              {h.status === 'active' && (
                <button onClick={() => revokeCredit(h.id)} disabled={saving}
                  className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-danger)] hover:bg-[var(--accent-danger)]/10 transition-smooth shrink-0 disabled:opacity-40">
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}